// ─────────────────────────────────────────────────────────────────
// Sehat AI — DoctorCard Component
// ─────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { MapPin, Clock, Banknote, Award, Phone, Navigation, Bookmark, BookmarkCheck, Loader2, X, Star, MessageSquare } from 'lucide-react';
import { SPECIALTY_ICONS } from '../data/specialties';
import { fetchPlaceDetails } from '../utils/placesApi';

function Stars({ rating, size = 12 }) {
  const filled = Math.round(rating);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={size}
          color={i <= filled ? '#F59E0B' : '#D1D5DB'}
          fill={i <= filled ? '#F59E0B' : 'none'}
          strokeWidth={1.5}
        />
      ))}
      <span style={{ color: '#6B7280', fontSize: size - 1, marginLeft: 4, fontWeight: 700 }}>
        {Number(rating).toFixed(1)}
      </span>
    </span>
  );
}

function Chip({ icon, text, color, bg, bold }) {
  return (
    <span style={{ fontSize: 11, color, background: bg, borderRadius: 8, padding: '4px 9px', fontWeight: bold ? 700 : 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {icon}{text}
    </span>
  );
}

function ReviewsModal({ doc, details, onClose }) {
  const reviews = details?.reviews || [];
  const rating  = details?.rating  ?? doc.rating;
  const total   = details?.user_ratings_total ?? doc.reviews;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        backdropFilter: 'blur(3px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: '24px 24px 0 0',
          width: '100%', maxWidth: 600, maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
          animation: 'slideUp 0.28s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 900, color: '#111827', fontFamily: "'Nunito',sans-serif" }}>{doc.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <Stars rating={rating} size={14} />
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  {total > 0 ? `${total.toLocaleString()} Google reviews` : 'No reviews yet'}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: '#F3F4F6', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            ><X size={15} color="#6B7280" /></button>
          </div>
        </div>

        {/* Reviews list */}
        <div style={{ overflowY: 'auto', padding: '12px 20px 24px', flex: 1 }}>
          {reviews.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, padding: '32px 0' }}>
              <MessageSquare size={32} color="#D1D5DB" style={{ margin: '0 auto 8px', display: 'block' }} />
              No review text available from Google
            </div>
          ) : reviews.map((r, i) => (
            <div key={i} style={{
              marginBottom: 14, padding: '13px 14px',
              background: '#FAFAFA', borderRadius: 14,
              border: '1px solid #F3F4F6',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                {/* Author avatar */}
                <div style={{
                  width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                  background: `hsl(${(r.author_name?.charCodeAt(0) ?? 200) % 360},60%,70%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 900, color: '#fff',
                }}>
                  {r.author_name?.[0]?.toUpperCase() ?? '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: '#111827', fontFamily: "'Nunito',sans-serif" }}>{r.author_name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Stars rating={r.rating} size={11} />
                    <span style={{ fontSize: 10.5, color: '#9CA3AF' }}>{r.relative_time_description}</span>
                  </div>
                </div>
              </div>
              {r.text ? (
                <p style={{ margin: 0, fontSize: 12.5, color: '#374151', lineHeight: 1.65 }}>{r.text}</p>
              ) : (
                <p style={{ margin: 0, fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>No text left</p>
              )}
            </div>
          ))}

          {reviews.length > 0 && (
            <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', marginTop: 4 }}>
              Showing top {reviews.length} reviews from Google Maps
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DoctorCard({ doc, idx, specialty }) {
  const [saved, setSaved] = useState(false);
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [showReviews, setShowReviews] = useState(false);

  async function ensureDetails() {
    if (details || !doc.isReal || !doc.placeId) return;
    setLoadingDetails(true);
    try {
      const d = await fetchPlaceDetails(doc.placeId);
      setDetails(d);
    } catch {
      // Silently fail
    }
    setLoadingDetails(false);
  }

  const handleCall = async () => {
    setShowPhone(true);
    await ensureDetails();
  };

  const handleReviews = async () => {
    await ensureDetails();
    setShowReviews(true);
  };

  const phone = details?.formatted_phone_number || doc.phone || null;
  const openNow = details
    ? details.opening_hours?.open_now
    : (doc.open ?? null);
  const availText = openNow === true ? 'Open Now'
    : openNow === false ? 'Closed Now'
    : doc.available || 'Call to confirm';
  const availColor = openNow === true ? '#059669'
    : openNow === false ? '#EF4444'
    : '#D97706';

  const reviewCount = details?.user_ratings_total ?? doc.reviews ?? 0;

  return (
    <>
      <div style={{
        background: '#FFFFFF',
        borderRadius: 18,
        padding: '15px 16px',
        marginBottom: 11,
        boxShadow: '0 3px 14px rgba(0,0,0,0.07)',
        border: '1px solid #F3F4F6',
        animation: `popUp 0.35s cubic-bezier(0.34,1.56,0.64,1) ${idx * 0.09}s both`,
        position: 'relative',
      }}>
        {/* LIVE badge */}
        {doc.isReal && (
          <div style={{ position: 'absolute', top: 12, right: 12, background: 'linear-gradient(135deg,#6366F1,#8B5CF6)', color: '#fff', fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 8, letterSpacing: 0.8 }}>
            📍 LIVE
          </div>
        )}

        {/* Doctor info */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
          <div style={{ width: 46, height: 46, borderRadius: 13, flexShrink: 0, background: 'linear-gradient(135deg,#FEF3C7,#FDE68A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
            {SPECIALTY_ICONS[specialty] || '🩺'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: '#111827', marginBottom: 2, fontFamily: "'Nunito',sans-serif" }}>{doc.name}</div>
            {doc.clinic && doc.clinic !== doc.name && (
              <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginBottom: 3 }}>{doc.clinic}</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
              {doc.rating && <Stars rating={doc.rating} />}
              {reviewCount > 0 && (
                <button
                  onClick={handleReviews}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontSize: 11, color: '#3B82F6', fontWeight: 700, textDecoration: 'underline',
                    fontFamily: "'Nunito',sans-serif",
                  }}
                >
                  {loadingDetails
                    ? <><Loader2 size={11} style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} /> Loading…</>
                    : `${reviewCount.toLocaleString()} reviews`}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 10 }}>
          <Chip icon={<MapPin size={10} />} text={doc.distance} color="#2563EB" bg="#EFF6FF" />
          <Chip icon={<Clock size={10} />} text={availText} color={availColor} bg={availColor + '15'} bold />
          {doc.fee && <Chip icon={<Banknote size={10} />} text={doc.fee} color="#059669" bg="#ECFDF5" bold />}
          {doc.exp && <Chip icon={<Award size={10} />} text={doc.exp} color="#7C3AED" bg="#F5F3FF" />}
        </div>

        {/* Address */}
        <div style={{ fontSize: 11.5, color: '#9CA3AF', marginBottom: 11, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
          <MapPin size={12} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{doc.address}</span>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleCall} style={{
            flex: 1, padding: '10px 12px', borderRadius: 12, border: 'none',
            background: showPhone ? '#059669' : 'linear-gradient(135deg,#10B981,#059669)',
            color: '#fff', fontSize: 12.5, cursor: 'pointer',
            fontFamily: "'Nunito',sans-serif", fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            boxShadow: showPhone ? 'none' : '0 4px 12px rgba(16,185,129,0.3)',
          }}>
            {loadingDetails && !showReviews
              ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</>
              : showPhone
                ? <><Phone size={13} /> {phone || 'Not Listed'}</>
                : <><Phone size={13} /> Call Now</>}
          </button>
          <button
            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(doc.name + ' ' + doc.address)}`, '_blank')}
            style={{
              flex: 1, padding: '10px 12px', borderRadius: 12,
              border: '1.5px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8',
              fontSize: 12.5, cursor: 'pointer',
              fontFamily: "'Nunito',sans-serif", fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            <Navigation size={13} /> Directions
          </button>
          <button onClick={() => setSaved(!saved)} style={{
            width: 42, borderRadius: 12,
            border: `1.5px solid ${saved ? '#F59E0B' : '#E5E7EB'}`,
            background: saved ? '#FFFBEB' : '#fff',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {saved
              ? <BookmarkCheck size={18} color="#F59E0B" />
              : <Bookmark size={18} color="#9CA3AF" />}
          </button>
        </div>
      </div>

      {/* Reviews popup — rendered outside card so it's not clipped */}
      {showReviews && (
        <ReviewsModal doc={doc} details={details} onClose={() => setShowReviews(false)} />
      )}
    </>
  );
}
